import type { JSX, ReactNode } from "react";
import type { Element } from "hast";
import { useChatContext } from "../../../context";

type ExtraProps = {
  node?: Element | undefined;
};

// Container for all follow-up elements
export function FollowupQuestions({
  node,
  children,
  ...props
}: JSX.IntrinsicElements["div"] & ExtraProps) {
  return (
    <div
      className="mt-6 flex flex-wrap gap-2"
      data-streamdown="followup-questions"
      {...props}
    >
      {children}
    </div>
  );
}

// Single clickable follow-up suggestion
export function FollowupQuestion({
  node,
  children,
  value,
  ...props
}: JSX.IntrinsicElements["button"] & ExtraProps & { value?: string }) {
  const { sendUserMessageFromSuggestion } = useChatContext();

  const handleClick = () => {
    const messageToSend = value || extractText(children);
    if (messageToSend) {
      sendUserMessageFromSuggestion(messageToSend);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="inline-flex items-center px-3 py-1.5 text-sm text-default-600 bg-default-50 hover:bg-default-100 ring-1 ring-default-200 rounded-full transition-colors cursor-pointer"
      data-streamdown="followup-question"
      {...props}
    >
      {children}
    </button>
  );
}

// Choice container with label (the question)
export function FollowupChoice({
  node,
  children,
  label,
  ...props
}: JSX.IntrinsicElements["div"] & ExtraProps & { label?: string }) {
  return (
    <div
      className="w-full flex flex-col gap-0 [&>p]:!my-2 mb-6"
      data-streamdown="followup-choice"
      {...props}
    >
      {label && <p className="text-slate-600 font-medium">{label}</p>}
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

// Single option within a choice
export function FollowupOption({
  node,
  children,
  value,
  ...props
}: JSX.IntrinsicElements["button"] & ExtraProps & { value?: string }) {
  const { sendUserMessageFromSuggestion } = useChatContext();

  const handleClick = () => {
    const messageToSend = value || extractText(children);
    if (messageToSend) {
      sendUserMessageFromSuggestion(messageToSend);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="inline-flex items-center px-3 py-1.5 text-sm text-default-600 bg-default-50 hover:bg-default-100 ring-1 ring-default-200 rounded-full transition-colors cursor-pointer"
      data-streamdown="followup-option"
      {...props}
    >
      {children}
    </button>
  );
}

function extractText(children: ReactNode): string {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(extractText).join("");
  if (children && typeof children === "object" && "props" in children) {
    return extractText(
      (children as { props: { children?: ReactNode } }).props.children,
    );
  }
  return "";
}
